import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type {
    WorkbookSheetSummary,
    WorkbookSheetColumn,
    SheetMapping,
    SegmentationOptions,
    GlobalOptions,
    Config,
    User
} from '../types';
import { PlanType } from '../types';
import {
    buildConfig,
    deriveGlobalOptions,
    deriveSegmentationOptions,
    deriveSheetMapping,
    parseConfig
} from '../utils/config';
import {
    describeWorkbook,
    extractXliff,
    mergeXliff,
    decodeFilePayload
} from '../api/client';
import yaml from 'js-yaml';
import { useAuth } from '../context/AuthContext';
import { AuthModal } from '../components/AuthModal';
import { useNavigate, useSearchParams } from 'react-router-dom';

const DEFAULT_GLOBAL_OPTIONS: GlobalOptions = {
    srcLang: 'en',
    exportComments: false,
    mergeFallback: 'source',
    insertTargetPlacement: 'insertAfterSource',
    overwrite: true,
    targetLocale: undefined,
    xliffVersion: '2.1'
};

const DEFAULT_SEGMENTATION: SegmentationOptions = {
    enabled: true,
    mode: 'builtin',
    srxText: undefined
};



const ANONYMOUS_LIMITS = {
    maxRowsPerFile: 10000,
    maxTargetLangs: 5,
    maxFileSize: 10 * 1024 * 1024 // 10MB
};

interface PanelProps {
    userProfile: User | null;
    refreshProfile: () => void;
}


function createDefaultMapping(summary: WorkbookSheetSummary): SheetMapping {
    return {
        sheetName: summary.name,
        enabled: true,
        sourceColumn: summary.columns[0]?.letter ?? 'A',
        sourceLocale: 'en',
        targetColumns: [],
        inlineCodeRegexes: [],
        headerRow: 1,
        valuesStartRow: 2,
        metadataRows: [],
        skipHiddenRows: true,
        skipHiddenColumns: true,
        translateComments: false,
        treatMerged: 'top-left',
        preserveStyles: false,
        excludeColors: [],
        htmlEnabled: true,
        htmlTranslatableTags: ''
    };
}

function updateSheetAtIndex(
    list: SheetMapping[],
    index: number,
    updates: Partial<SheetMapping>
): SheetMapping[] {
    return list.map((sheet, idx) => (idx === index ? { ...sheet, ...updates } : sheet));
}

function metadataRowsToString(rows: number[]): string {
    return rows.length ? rows.join(', ') : '';
}

function parseNumberList(value: string): number[] {
    return value
        .split(',')
        .map((item) => parseInt(item.trim(), 10))
        .filter((n) => !Number.isNaN(n) && n > 0);
}

function parseStringList(value: string): string[] {
    return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
}

function useBlobUrl(blob: Blob | null): string | null {
    const [url, setUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!blob) {
            if (url) URL.revokeObjectURL(url);
            setUrl(null);
            return;
        }
        const nextUrl = URL.createObjectURL(blob);
        setUrl(nextUrl);
        return () => URL.revokeObjectURL(nextUrl);
    }, [blob]);
    return url;
}

function mapDescribeResponseToSummary(response: Awaited<ReturnType<typeof describeWorkbook>>): WorkbookSheetSummary[] {
    return response.sheets.map((sheet) => ({
        name: sheet.name,
        rowCount: sheet.rowCount,
        headerRowValues: sheet.columns.map((col) => col.header ?? ''),
        columns: sheet.columns,
        previewRows: sheet.previewRows
    }));
}

const ExcelToXliffPanel: React.FC<PanelProps> = ({ userProfile, refreshProfile }) => {
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

    const [workbookFile, setWorkbookFile] = useState<File | null>(null);
    const [srxFile, setSrxFile] = useState<File | null>(null);
    const [sheetSummaries, setSheetSummaries] = useState<WorkbookSheetSummary[]>([]);
    const [sheetMappings, setSheetMappings] = useState<SheetMapping[]>([]);
    const [globalOptions, setGlobalOptions] = useState<GlobalOptions>(DEFAULT_GLOBAL_OPTIONS);
    const [segmentation, setSegmentation] = useState<SegmentationOptions>(DEFAULT_SEGMENTATION);
    const [isLoading, setIsLoading] = useState(false);
    const [configImportText, setConfigImportText] = useState('');
    const [configJson, setConfigJson] = useState<string | null>(null);
    const [extractedBlob, setExtractedBlob] = useState<Blob | null>(null);
    const [downloadFileName, setDownloadFileName] = useState<string>('translation.xlf');

    const downloadUrl = useBlobUrl(extractedBlob);

    const [sheetsImportProcessed, setSheetsImportProcessed] = useState(false);

    useEffect(() => {
        const handleSheetsImport = async () => {
            const source = searchParams.get('source');
            const fileId = searchParams.get('fileId');
            const fileName = searchParams.get('fileName');

            // Only process if coming from Google Sheets and not already processed
            if (source !== 'sheets' || !fileId || sheetsImportProcessed) {
                return;
            }

            // Check if user is logged in
            if (!user) {
                setIsAuthModalOpen(true);
                return;
            }

            // Mark as processed to prevent re-runs
            setSheetsImportProcessed(true);

            const toastId = toast.loading('Downloading your sheet...');

            try {
                // Use backend proxy to avoid CORS
                const proxyUrl = `${import.meta.env.VITE_API_BASE_URL}/proxy/drive?fileId=${encodeURIComponent(fileId)}`;
                const response = await fetch(proxyUrl);

                if (!response.ok) {
                    throw new Error('Failed to download sheet');
                }

                const blob = await response.blob();
                const file = new File(
                    [blob],
                    (fileName || 'sheet') + '.xlsx',
                    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
                );

                // Analyze the workbook (same as handleWorkbookUpload)
                toast.loading('Analyzing workbook...', { id: toastId });
                const description = await describeWorkbook(file);
                const summaries = mapDescribeResponseToSummary(description);

                setWorkbookFile(file);
                setSheetSummaries(summaries);

                // Auto-detect mapping
                const mappings = summaries.map(summary => {
                    const mapping = createDefaultMapping(summary);
                    const sourceCol = detectSourceColumn(summary.columns);
                    mapping.sourceColumn = sourceCol;
                    const detectedTargets = detectTargetColumns(summary.columns, sourceCol);
                    const maxTargets = userProfile ? userProfile.limits.maxTargetLangs : ANONYMOUS_LIMITS.maxTargetLangs;
                    if (detectedTargets.length > maxTargets) {
                        mapping.targetColumns = detectedTargets.slice(0, maxTargets);
                    } else if (detectedTargets.length > 0) {
                        mapping.targetColumns = detectedTargets;
                    }
                    return mapping;
                });

                setSheetMappings(mappings);
                setConfigJson(null);
                setExtractedBlob(null);

                toast.success(`Loaded ${file.name}`, { id: toastId });

                // Clean up URL
                window.history.replaceState({}, '', '/convert');

            } catch (error) {
                console.error('Sheets import error:', error);
                toast.error('Failed to load sheet from Google Sheets', { id: toastId });
                setSheetsImportProcessed(false); // Allow retry on error
            }
        };

        handleSheetsImport();
    }, [searchParams, user, sheetsImportProcessed, userProfile]);



    useEffect(() => {
        if (segmentation.mode !== 'srx') {
            setSrxFile(null);
        }
    }, [segmentation.mode]);

    const LANGUAGE_NAME_MAP: Record<string, string> = {
        // Common
        'english': 'en',
        'french': 'fr', 'français': 'fr', 'francais': 'fr',
        'german': 'de', 'deutsch': 'de',
        'spanish': 'es', 'español': 'es', 'espanol': 'es',
        'italian': 'it', 'italiano': 'it',
        'portuguese': 'pt', 'português': 'pt', 'portugues': 'pt',
        'dutch': 'nl', 'nederlands': 'nl',
        'russian': 'ru', 'русский': 'ru',
        'chinese': 'zh', '中文': 'zh', 'hanyu': 'zh', '汉语': 'zh',
        'japanese': 'ja', '日本語': 'ja', 'nihongo': 'ja',
        'korean': 'ko', '한국어': 'ko', 'hangugeo': 'ko',
        'arabic': 'ar', 'العربية': 'ar',
        'hindi': 'hi', 'हिन्दी': 'hi',
        'turkish': 'tr', 'türkçe': 'tr', 'turkce': 'tr',
        'polish': 'pl', 'polski': 'pl',
        'swedish': 'sv', 'svenska': 'sv',
        'danish': 'da', 'dansk': 'da',
        'finnish': 'fi', 'suomi': 'fi',
        'norwegian': 'no', 'norsk': 'no',
        'czech': 'cs', 'čeština': 'cs', 'cestina': 'cs',
        'hungarian': 'hu', 'magyar': 'hu',
        'romanian': 'ro', 'română': 'ro', 'romana': 'ro',
        'thai': 'th', 'ไทย': 'th',
        'vietnamese': 'vi', 'tiếng việt': 'vi', 'tieng viet': 'vi',
        'indonesian': 'id', 'bahasa indonesia': 'id',
        'ukrainian': 'uk', 'українська': 'uk',
        'greek': 'el', 'ελληνικά': 'el',
        'hebrew': 'he', 'עברית': 'he',

        // Extended Europe
        'albanian': 'sq', 'shqip': 'sq',
        'armenian': 'hy', 'հայերեն': 'hy',
        'basque': 'eu', 'euskara': 'eu',
        'belarusian': 'be', 'беларуская': 'be',
        'bosnian': 'bs', 'bosanski': 'bs',
        'bulgarian': 'bg', 'български': 'bg',
        'catalan': 'ca', 'català': 'ca', 'catala': 'ca',
        'croatian': 'hr', 'hrvatski': 'hr',
        'estonian': 'et', 'eesti': 'et',
        'galician': 'gl', 'galego': 'gl',
        'georgian': 'ka', 'ქართული': 'ka',
        'icelandic': 'is', 'íslenska': 'is', 'islenska': 'is',
        'irish': 'ga', 'gaeilge': 'ga',
        'latvian': 'lv', 'latviešu': 'lv',
        'lithuanian': 'lt', 'lietuvių': 'lt',
        'macedonian': 'mk', 'македонски': 'mk',
        'maltese': 'mt', 'malti': 'mt',
        'serbian': 'sr', 'српски': 'sr',
        'slovak': 'sk', 'slovenčina': 'sk', 'slovencina': 'sk',
        'slovenian': 'sl', 'slovenščina': 'sl', 'slovenscina': 'sl',
        'welsh': 'cy', 'cymraeg': 'cy',

        // Extended Asia/Pacific
        'azerbaijani': 'az', 'azərbaycan': 'az',
        'bengali': 'bn', 'বাংলা': 'bn',
        'burmese': 'my', 'မြန်မာစာ': 'my',
        'cambodian': 'km', 'khmer': 'km', 'ភាសាខ្មែរ': 'km',
        'filipino': 'fil', 'tagalog': 'tl',
        'gujarati': 'gu', 'ગુજરાતી': 'gu',
        'kazakh': 'kk', 'қазақ': 'kk',
        'lao': 'lo', 'ລາວ': 'lo',
        'malay': 'ms', 'bahasa melayu': 'ms',
        'malayalam': 'ml', 'മലയാളം': 'ml',
        'marathi': 'mr', 'मराठी': 'mr',
        'mongolian': 'mn', 'монгол': 'mn',
        'nepali': 'ne', 'नेपाली': 'ne',
        'punjabi': 'pa', 'ਪੰਜਾਬੀ': 'pa',
        'sinhala': 'si', 'සිංහල': 'si',
        'tamil': 'ta', 'தமிழ்': 'ta',
        'telugu': 'te', 'తెలుగు': 'te',
        'urdu': 'ur', 'اردو': 'ur',
        'uzbek': 'uz', 'oʻzbek': 'uz',

        // Middle East / Africa
        'amharic': 'am', 'አማርኛ': 'am',
        'hausa': 'ha',
        'igbo': 'ig',
        'kurdish': 'ku', 'kurdî': 'ku',
        'pashto': 'ps', 'پښتو': 'ps',
        'persian': 'fa', 'farsi': 'fa', 'فارسی': 'fa',
        'somali': 'so', 'soomaaliga': 'so',
        'swahili': 'sw', 'kiswahili': 'sw',
        'yoruba': 'yo', 'yorùbá': 'yo',
        'zulu': 'zu', 'isizulu': 'zu',

        // Americas
        'quechua': 'qu',
        'guarani': 'gn'
    };

    const detectSourceColumn = (columns: WorkbookSheetColumn[]): string => {
        // Priority 1: Explicit "Source", "Term", "String"
        const explicitKeywords = ['source', 'term', 'string', 'text'];
        for (const col of columns) {
            if (col.header && explicitKeywords.includes(col.header.trim().toLowerCase())) {
                return col.letter;
            }
        }

        // Priority 2: Match global source language (default 'en')
        const srcLang = globalOptions.srcLang.toLowerCase();
        for (const col of columns) {
            if (!col.header) continue;
            const header = col.header.trim().toLowerCase();
            // Check code
            if (header === srcLang) return col.letter;
            // Check name
            if (LANGUAGE_NAME_MAP[header] === srcLang) return col.letter;
            // Check "English" if src is 'en'
            if (srcLang === 'en' && header.includes('english')) return col.letter;
        }

        // Priority 3: First column
        return columns[0]?.letter ?? 'A';
    };

    const detectTargetColumns = (columns: WorkbookSheetColumn[], sourceColLetter: string): Array<{ locale: string; column: string }> => {
        const targets: Array<{ locale: string; column: string }> = [];
        // Simple heuristic: 2-letter or 5-letter codes (e.g., "fr", "de-DE")
        const localeRegex = /^[a-z]{2}(-[A-Z]{2})?$/;

        for (const col of columns) {
            if (col.letter === sourceColLetter) continue; // Skip source column
            if (!col.header) continue;

            const header = col.header.trim();
            const lowerHeader = header.toLowerCase();

            // 1. Check if it's a known language name
            if (LANGUAGE_NAME_MAP[lowerHeader]) {
                targets.push({ locale: LANGUAGE_NAME_MAP[lowerHeader], column: col.letter });
                continue;
            }

            // 2. Check if it looks like a locale code
            if (localeRegex.test(header)) {
                targets.push({ locale: header, column: col.letter });
                continue;
            }
        }
        return targets;
    };

    const handleWorkbookUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // RESTRICTION: File size check
        const maxFileSize = userProfile ? (userProfile.currentPlan === PlanType.PRO ? 30 * 1024 * 1024 : 20 * 1024 * 1024) : ANONYMOUS_LIMITS.maxFileSize;
        if (file.size > maxFileSize) {
            toast.error(`File size limit exceeded: Max ${maxFileSize / (1024 * 1024)}MB.`);
            event.target.value = ''; // Reset input
            return;
        }

        const toastId = toast.loading('Analyzing workbook…');
        try {
            const description = await describeWorkbook(file);
            const summaries = mapDescribeResponseToSummary(description);
            setWorkbookFile(file);
            setSheetSummaries(summaries);

            // Auto-detect mapping
            const mappings = summaries.map(summary => {
                const mapping = createDefaultMapping(summary);

                // Smart Source Detection
                const sourceCol = detectSourceColumn(summary.columns);
                mapping.sourceColumn = sourceCol;

                // Smart Target Detection
                const detectedTargets = detectTargetColumns(summary.columns, sourceCol);

                // RESTRICTION: Max target languages
                const maxTargets = userProfile ? userProfile.limits.maxTargetLangs : ANONYMOUS_LIMITS.maxTargetLangs;
                if (detectedTargets.length > maxTargets) {
                    // Take only allowed number
                    mapping.targetColumns = detectedTargets.slice(0, maxTargets);
                } else if (detectedTargets.length > 0) {
                    mapping.targetColumns = detectedTargets;
                }

                return mapping;
            });

            setSheetMappings(mappings);
            setConfigJson(null);
            setExtractedBlob(null);

            // Update the loading toast to success
            toast.success(`Loaded ${file.name}`, { id: toastId });
        } catch (error) {
            // Update the loading toast to error
            toast.error((error as Error).message || 'Failed to analyze workbook.', { id: toastId });
        }
    };

    const handleSegmentationFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        const file = event.target.files?.[0] ?? null;
        setSrxFile(file);
    };

    const parseConfigInput = (input: string): Config => {
        try {
            return JSON.parse(input);
        } catch (e) {
            try {
                return yaml.load(input) as Config;
            } catch (e2) {
                throw new Error('Invalid JSON or YAML format.');
            }
        }
    };

    const handleConfigUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            setConfigImportText(text);
            applyConfigImport(text);
        } catch (error) {
            toast.error((error as Error).message || 'Failed to read config file.'
            );
        }
    };

    const applyConfigImport = (textOverride?: string) => {
        const text = textOverride ?? configImportText;
        if (!text.trim()) return;
        try {
            const parsed = parseConfigInput(text);
            setGlobalOptions(deriveGlobalOptions(parsed));
            setSegmentation((prev) => deriveSegmentationOptions(parsed, prev));

            if (sheetSummaries.length) {
                const mapped = sheetSummaries.map((summary) => {
                    const match = parsed.workbook.sheets.find((cfg) => cfg.namePattern === summary.name);
                    if (match) {
                        const derived = deriveSheetMapping(match);
                        return { ...createDefaultMapping(summary), ...derived };
                    }
                    return createDefaultMapping(summary);
                });
                setSheetMappings(mapped);
            }
            toast.success('Configuration imported.');
        } catch (error) {
            toast.error((error as Error).message || 'Failed to parse configuration.'
            );
        }
    };

    const generateConfig = () => {
        return buildConfig(sheetMappings, globalOptions, segmentation).config;
    };

    const handleDownloadConfig = (format: 'json' | 'yaml') => {
        const config = generateConfig();
        let content = '';
        let filename = 'config';
        let type = '';

        if (format === 'json') {
            content = JSON.stringify(config, null, 2);
            filename += '.json';
            type = 'application/json';
        } else {
            // @ts-ignore
            content = yaml.dump(config);
            filename += '.yaml';
            type = 'text/yaml';
        }

        const blob = new Blob([content], { type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleExtract = async () => {
        if (!workbookFile) {
            toast.error('Upload a workbook first.');
            return;
        }
        const enabledSheets = sheetMappings.filter((sheet) => sheet.enabled && sheet.sourceColumn.trim());
        if (!enabledSheets.length) {
            toast.error('Enable at least one sheet and choose a source column.');
            return;
        }



        // Check row limits
        const maxRows = userProfile ? userProfile.limits.maxRowsPerFile : ANONYMOUS_LIMITS.maxRowsPerFile;
        const totalRows = sheetSummaries.reduce((acc, s) => acc + s.rowCount, 0); // Approximate
        // Actually we should check per sheet or total? Backend checks per file.
        // Let's check max of any sheet for now or just warn.
        // Backend enforces it strictly.
        if (totalRows > maxRows) {
            toast.error(`Row limit exceeded. Max ${maxRows} rows allowed. Upgrade plan for more.`);
            return;
        }

        const toastId = toast.loading('Converting to XLIFF…');
        try {
            const { serialized } = buildConfig(sheetMappings, globalOptions, segmentation);
            const response = await extractXliff({
                workbook: workbookFile,
                configJson: serialized,
                srxFile: segmentation.mode === 'srx' ? srxFile : null,
                token: user?.token
            });
            const blob = decodeFilePayload(response);
            setExtractedBlob(blob);
            setDownloadFileName(response.fileName || 'translation.xlf');
            setConfigJson(serialized);
            if (userProfile) refreshProfile();

            // Auto-download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = response.fileName || 'translation.xlf';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            // Update loading toast to success
            toast.success('Conversion successful.', { id: toastId });
        } catch (error) {
            // Handle specific status codes if client throws them, or generic message
            toast.error((error as Error).message || 'Conversion failed.', { id: toastId });
        }
    };

    return (
        <div className="vertical-layout">
            <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />

            <section className="card">
                <h2>Workbook</h2>
                <div className="row-group">
                    <div className="field flex-grow">
                        <label htmlFor="extract-workbook">Excel workbook (.xlsx)</label>
                        <input id="extract-workbook" type="file" accept=".xlsx" onChange={handleWorkbookUpload} />
                        {workbookFile && (
                            <div className="message success">
                                Loaded <strong>{workbookFile.name}</strong> — {sheetSummaries.length} sheet
                                {sheetSummaries.length === 1 ? '' : 's'} detected.
                            </div>
                        )}
                    </div>
                </div>



            </section>

            <section className="card">
                <h2>Configuration</h2>
                <div className="field flex-grow">
                    <label>Import Config in JSON or YAML (optional)</label>
                    <div className="input-group">
                        <input type="file" accept=".json,.yml,.yaml" onChange={handleConfigUpload} />
                        <span className="separator">OR</span>
                        <textarea
                            placeholder="Paste config JSON or YAML here..."
                            value={configImportText}
                            onChange={(event) => setConfigImportText(event.target.value)}
                            rows={1}
                            style={{ minHeight: '38px', resize: 'vertical' }}
                        />
                        <button className="button secondary" type="button" onClick={() => applyConfigImport()}>
                            Apply
                        </button>
                    </div>
                </div>
                <div className="row-group two-col">
                    <div className="field">
                        <label htmlFor="extract-src-lang">Source language</label>
                        <input
                            id="extract-src-lang"
                            type="text"
                            value={globalOptions.srcLang}
                            onChange={(event) => setGlobalOptions((prev) => ({ ...prev, srcLang: event.target.value }))}
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="extract-target-locale">Preferred target locale (optional)</label>
                        <input
                            id="extract-target-locale"
                            type="text"
                            value={globalOptions.targetLocale ?? ''}
                            onChange={(event) =>
                                setGlobalOptions((prev) => ({
                                    ...prev,
                                    targetLocale: event.target.value.trim() ? event.target.value : undefined
                                }))
                            }
                        />
                    </div>
                </div>



                <div className="divider" />

                <div className="row-group comments-box">
                    <div className="field checkbox-field">
                        <label htmlFor="extract-export-comments">
                            <input
                                id="extract-export-comments"
                                type="checkbox"
                                checked={globalOptions.exportComments}
                                onChange={(event) =>
                                    setGlobalOptions((prev) => ({ ...prev, exportComments: event.target.checked }))
                                }
                            />
                            Export comments to XLIFF notes
                        </label>
                    </div>
                </div>

                <div className="divider" />

                <div className="row-group segmentation-container">
                    <div className="field checkbox-field">
                        <label htmlFor="extract-seg-enabled">
                            <input
                                id="extract-seg-enabled"
                                type="checkbox"
                                checked={segmentation.enabled}
                                onChange={(event) => setSegmentation((prev) => ({ ...prev, enabled: event.target.checked }))}
                            />
                            Enable segmentation
                        </label>


                        {segmentation.enabled && (
                            <>
                                <div className="field">
                                    <label htmlFor="extract-seg-mode">Segmentation rules</label>
                                    <select
                                        id="extract-seg-mode"
                                        value={segmentation.mode}
                                        onChange={(event) =>
                                            setSegmentation((prev) => ({ ...prev, mode: event.target.value as 'builtin' | 'srx' }))
                                        }
                                    >
                                        <option value="builtin">Built-in</option>
                                        <option value="srx" disabled={userProfile ? !userProfile.limits.customSrx : true}>
                                            Custom SRX {(!userProfile || !userProfile.limits.customSrx) && '(Upgrade required)'}
                                        </option>
                                    </select>
                                </div>
                                {segmentation.mode === 'srx' && (
                                    <div className="field">
                                        <label htmlFor="extract-srx">SRX rules file</label>
                                        <input id="extract-srx" type="file" accept=".srx,.xml" onChange={handleSegmentationFileChange} />
                                        {srxFile && <small>Loaded: {srxFile.name}</small>}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section >

            <section className="card">
                <h2>Sheet Mapping</h2>
                {sheetSummaries.length > 0 ? (
                    <div className="sheet-list">
                        {sheetSummaries.map((summary, index) => {
                            const mapping = sheetMappings[index] ?? createDefaultMapping(summary);
                            return (
                                <div className="sheet-card full-width" key={summary.name}>
                                    <div className="sheet-card-header">
                                        <div className="sheet-info">
                                            <strong>{summary.name}</strong>
                                            <span className="badge">{summary.rowCount} rows</span>
                                        </div>
                                        <label className="toggle-row">
                                            <span>Include in export</span>
                                            <input
                                                type="checkbox"
                                                checked={mapping.enabled}
                                                onChange={(event) =>
                                                    setSheetMappings((prev) =>
                                                        updateSheetAtIndex(prev, index, { enabled: event.target.checked })
                                                    )
                                                }
                                            />
                                        </label>
                                    </div>

                                    {mapping.enabled && (
                                        <div className="sheet-card-body">
                                            {/* Preview Table */}
                                            {summary.previewRows.length > 0 && (
                                                <div className="preview-table-wrapper">
                                                    <table className="preview-table">
                                                        <thead>
                                                            <tr>
                                                                {summary.columns.map(col => (
                                                                    <th key={col.letter}>{col.letter}<br /><small>{col.header}</small></th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {summary.previewRows.map((row, rIdx) => (
                                                                <tr key={rIdx}>
                                                                    {summary.columns.map(col => (
                                                                        <td key={col.letter}>{row[col.letter] || ''}</td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}

                                            <div className="mapping-grid">
                                                <div className="field">
                                                    <label>Source column</label>
                                                    <select
                                                        value={mapping.sourceColumn}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, { sourceColumn: event.target.value })
                                                            )
                                                        }
                                                    >
                                                        {summary.columns.map((col) => (
                                                            <option key={col.letter} value={col.letter}>
                                                                {col.letter} {col.header ? `— ${col.header}` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="field">
                                                    <label>Source locale</label>
                                                    <input
                                                        type="text"
                                                        value={mapping.sourceLocale}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, { sourceLocale: event.target.value })
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="field full-row">
                                                    <label>Target locales</label>
                                                    <div className="pill-group">
                                                        {mapping.targetColumns.map((target, tIndex) => (
                                                            <div className="chip" key={tIndex}>
                                                                <input
                                                                    type="text"
                                                                    placeholder="locale"
                                                                    value={target.locale}
                                                                    onChange={(event) => {
                                                                        const next = [...mapping.targetColumns];
                                                                        next[tIndex] = { ...next[tIndex], locale: event.target.value };
                                                                        setSheetMappings((prev) =>
                                                                            updateSheetAtIndex(prev, index, { targetColumns: next })
                                                                        );
                                                                    }}
                                                                />
                                                                <select
                                                                    value={target.column}
                                                                    onChange={(event) => {
                                                                        const next = [...mapping.targetColumns];
                                                                        next[tIndex] = { ...next[tIndex], column: event.target.value };
                                                                        setSheetMappings((prev) =>
                                                                            updateSheetAtIndex(prev, index, { targetColumns: next })
                                                                        );
                                                                    }}
                                                                >
                                                                    <option value="">Select column</option>
                                                                    {summary.columns.map((col) => (
                                                                        <option key={col.letter} value={col.letter}>
                                                                            {col.letter}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <button
                                                                    type="button"
                                                                    className="icon-button small"
                                                                    onClick={() => {
                                                                        const next = mapping.targetColumns.filter((_, i) => i !== tIndex);
                                                                        setSheetMappings((prev) =>
                                                                            updateSheetAtIndex(prev, index, { targetColumns: next })
                                                                        );
                                                                    }}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                        <button
                                                            className="button secondary small"
                                                            type="button"
                                                            onClick={() => {
                                                                const maxTargets = userProfile ? userProfile.limits.maxTargetLangs : ANONYMOUS_LIMITS.maxTargetLangs;
                                                                if (mapping.targetColumns.length >= maxTargets) {
                                                                    toast.error(`Limit reached: Max ${maxTargets} target languages.`);
                                                                    return;
                                                                }
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, {
                                                                        targetColumns: [...mapping.targetColumns, { locale: '', column: '' }]
                                                                    })
                                                                );
                                                            }}
                                                        >
                                                            + Add
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="field">
                                                    <label>Header row</label>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        value={mapping.headerRow}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, {
                                                                    headerRow: Number(event.target.value) || 1
                                                                })
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="field">
                                                    <label>First content row</label>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        value={mapping.valuesStartRow}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, {
                                                                    valuesStartRow: Number(event.target.value) || 2
                                                                })
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="field">
                                                    <label>Metadata rows</label>
                                                    <input
                                                        type="text"
                                                        placeholder="e.g. 1, 2"
                                                        value={metadataRowsToString(mapping.metadataRows)}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, {
                                                                    metadataRows: parseNumberList(event.target.value)
                                                                })
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="field">
                                                    <label>Exclude colors</label>
                                                    <input
                                                        type="text"
                                                        placeholder="#FF0000, #00FF00"
                                                        value={mapping.excludeColors.join(', ')}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, {
                                                                    excludeColors: parseStringList(event.target.value)
                                                                })
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="field">
                                                    <label>Inline placeholder regex</label>
                                                    <input
                                                        type="text"
                                                        placeholder="\\{\\d+\\}, %s"
                                                        value={mapping.inlineCodeRegexes.join(', ')}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, {
                                                                    inlineCodeRegexes: parseStringList(event.target.value)
                                                                })
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="field">
                                                    <label>Translatable HTML tags</label>
                                                    <input
                                                        type="text"
                                                        placeholder="span, strong, em"
                                                        value={mapping.htmlTranslatableTags}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, { htmlTranslatableTags: event.target.value })
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <div className="field full-row checkboxes-grid">
                                                    <label className="checkbox-label">
                                                        Skip hidden rows
                                                        <input
                                                            type="checkbox"
                                                            checked={mapping.skipHiddenRows}
                                                            onChange={(event) =>
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, { skipHiddenRows: event.target.checked })
                                                                )
                                                            }
                                                        />
                                                    </label>
                                                    <label className="checkbox-label">
                                                        Skip hidden columns
                                                        <input
                                                            type="checkbox"
                                                            checked={mapping.skipHiddenColumns}
                                                            onChange={(event) =>
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, { skipHiddenColumns: event.target.checked })
                                                                )
                                                            }
                                                        />
                                                    </label>
                                                    <label className="checkbox-label">
                                                        Translate comments
                                                        <input
                                                            type="checkbox"
                                                            checked={mapping.translateComments}
                                                            onChange={(event) =>
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, { translateComments: event.target.checked })
                                                                )
                                                            }
                                                        />
                                                    </label>
                                                    <label className="checkbox-label">
                                                        Preserve styles
                                                        <input
                                                            type="checkbox"
                                                            checked={mapping.preserveStyles}
                                                            onChange={(event) =>
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, { preserveStyles: event.target.checked })
                                                                )
                                                            }
                                                        />
                                                    </label>
                                                    <label className="checkbox-label">
                                                        HTML-aware
                                                        <input
                                                            type="checkbox"
                                                            checked={mapping.htmlEnabled}
                                                            onChange={(event) =>
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, { htmlEnabled: event.target.checked })
                                                                )
                                                            }
                                                        />
                                                    </label>
                                                </div>

                                                <div className="field">
                                                    <label>Merged regions</label>
                                                    <select
                                                        value={mapping.treatMerged ?? 'top-left'}
                                                        onChange={(event) =>
                                                            setSheetMappings((prev) =>
                                                                updateSheetAtIndex(prev, index, {
                                                                    treatMerged: event.target.value as SheetMapping['treatMerged']
                                                                })
                                                            )
                                                        }
                                                    >
                                                        <option value="top-left">Top-left only</option>
                                                        <option value="expand">Expand</option>
                                                        <option value="skip">Skip merged</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="card placeholder">
                        <p>Upload a workbook to configure sheet mapping.</p>
                    </div>
                )}
            </section>

            <section className="card">
                <h2>Conversion</h2>
                {userProfile && (
                    <div className="info-box" style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', gap: '2rem', marginBottom: '0.5rem' }}>
                            <div>
                                <strong>Plan:</strong> {userProfile.currentPlan ? userProfile.currentPlan.charAt(0).toUpperCase() + userProfile.currentPlan.slice(1) : 'Free'}
                            </div>
                            <div>
                                <strong>Max Rows/File:</strong> {userProfile.limits?.maxRowsPerFile === 999999 ? 'Unlimited' : (userProfile.limits?.maxRowsPerFile || 200)}
                            </div>
                            <div>
                                <strong>Max Target Langs:</strong> {userProfile.limits?.maxTargetLangs === 999 ? 'Unlimited' : (userProfile.limits?.maxTargetLangs ?? 4)}
                            </div>
                        </div>
                    </div>
                )}
                {!userProfile && (
                    <div className="info-box" style={{ marginBottom: '1rem', background: 'rgba(22, 163, 74, 0.05)' }}>
                        <p><strong>Anonymous Limits:</strong></p>
                        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                            <li>Max {ANONYMOUS_LIMITS.maxRowsPerFile} rows per file</li>
                            <li>Max {ANONYMOUS_LIMITS.maxTargetLangs} target languages</li>
                            <li>Max 10MB file size</li>
                            <li>1 conversion per day</li>
                        </ul>
                        <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                            <strong>Sign up for free</strong> to get higher limits!
                        </p>
                    </div>
                )}
                <div className="panel-actions">
                    <button
                        className="button large"
                        onClick={handleExtract}
                    >
                        Convert to XLIFF
                    </button>
                    <button className="button large" onClick={() => handleDownloadConfig('json')}>
                        Download Config JSON
                    </button>
                    <button className="button large" onClick={() => handleDownloadConfig('yaml')}>
                        Download Config YAML
                    </button>
                </div>

                <div className="info-box">
                    <h3>How to use the exported files</h3>
                    <p>
                        The converter generates XLIFF files (1.2 or 2.1), which are the industry standard for localization.
                        You can import these files into any CAT (Computer-Aided Translation) tool like Trados, MemoQ, or Phrase.
                    </p>
                    <p>
                        <strong>Config File:</strong> The configuration file (JSON or YAML) saves your current settings, including sheet mappings and segmentation rules.
                        Keep this file to easily restore your setup for future updates to the same workbook.
                    </p>
                </div>

                <div className="results">
                    <label>Config JSON Preview</label>
                    <textarea readOnly value={JSON.stringify(generateConfig(), null, 2)} rows={10} />
                    <button
                        className="button small"
                        style={{ marginTop: '0.5rem' }}
                        onClick={() => navigator.clipboard.writeText(JSON.stringify(generateConfig(), null, 2))}
                    >
                        Copy to Clipboard
                    </button>
                </div>
            </section>
        </div >
    );
};

const XliffToExcelPanel: React.FC<PanelProps> = ({ userProfile, refreshProfile }) => {
    const { user } = useAuth();
    const [referenceFile, setReferenceFile] = useState<File | null>(null);
    const [xliffFiles, setXliffFiles] = useState<File[]>([]);
    const [srxFile, setSrxFile] = useState<File | null>(null);
    const [sheetSummaries, setSheetSummaries] = useState<WorkbookSheetSummary[]>([]);
    const [sheetMappings, setSheetMappings] = useState<SheetMapping[]>([]);
    const [globalOptions, setGlobalOptions] = useState<GlobalOptions>(DEFAULT_GLOBAL_OPTIONS);
    const [segmentation, setSegmentation] = useState<SegmentationOptions>(DEFAULT_SEGMENTATION);
    const [isLoading, setIsLoading] = useState(false);
    const [configImportText, setConfigImportText] = useState('');
    const [configJson, setConfigJson] = useState<string | null>(null);
    const [mergedBlob, setMergedBlob] = useState<Blob | null>(null);
    const [downloadFileName, setDownloadFileName] = useState<string>('localized.xlsx');

    const downloadUrl = useBlobUrl(mergedBlob);



    useEffect(() => {
        if (segmentation.mode !== 'srx') {
            setSrxFile(null);
        }
    }, [segmentation.mode]);

    const handleReferenceUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        // RESTRICTION: File size check
        const maxFileSize = userProfile ? (userProfile.currentPlan === PlanType.PRO ? 30 * 1024 * 1024 : 20 * 1024 * 1024) : ANONYMOUS_LIMITS.maxFileSize;
        if (file.size > maxFileSize) {
            toast.error(`File size limit exceeded: Max ${maxFileSize / (1024 * 1024)}MB.`);
            event.target.value = ''; // Reset input
            return;
        }

        const toastId = toast.loading('Analyzing reference workbook…');
        try {
            const description = await describeWorkbook(file);
            const summaries = mapDescribeResponseToSummary(description);
            setReferenceFile(file);
            setSheetSummaries(summaries);
            setSheetMappings(summaries.map(createDefaultMapping));
            setMergedBlob(null);
            setConfigJson(null);

            // Update loading toast to success
            toast.success(`Loaded ${file.name}`, { id: toastId });
        } catch (error) {
            // Update loading toast to error
            toast.error((error as Error).message || 'Failed to analyze workbook.', { id: toastId });
        }
    };

    const parseConfigInput = (input: string): Config => {
        try {
            return JSON.parse(input);
        } catch (e) {
            try {
                return yaml.load(input) as Config;
            } catch (e2) {
                throw new Error('Invalid JSON or YAML format.');
            }
        }
    };

    const handleConfigUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            setConfigImportText(text);
            applyConfigImport(text);
        } catch (error) {
            toast.error((error as Error).message || 'Failed to read config file.'
            );
        }
    };

    const applyConfigImport = (textOverride?: string) => {
        const text = textOverride ?? configImportText;
        if (!text.trim()) return;
        try {
            const parsed = parseConfigInput(text);
            setGlobalOptions(deriveGlobalOptions(parsed));
            setSegmentation((prev) => deriveSegmentationOptions(parsed, prev));

            if (sheetSummaries.length) {
                const mapped = sheetSummaries.map((summary) => {
                    const match = parsed.workbook.sheets.find((cfg) => cfg.namePattern === summary.name);
                    if (match) {
                        const derived = deriveSheetMapping(match);
                        return { ...createDefaultMapping(summary), ...derived };
                    }
                    return createDefaultMapping(summary);
                });
                setSheetMappings(mapped);
            }
            setConfigJson(JSON.stringify(parsed, null, 2));
            toast.success('Configuration imported.');
        } catch (error) {
            toast.error((error as Error).message || 'Failed to parse configuration.'
            );
        }
    };

    const runMerge = async () => {
        if (!referenceFile) {
            toast.error('Upload the reference workbook first.');
            return;
        }
        if (!xliffFiles.length) {
            toast.error('Upload at least one translated XLIFF file.');
            return;
        }



        const toastId = toast.loading('Merging translations…');
        try {
            const { serialized } = buildConfig(sheetMappings, globalOptions, segmentation);
            const response = await mergeXliff({
                workbook: referenceFile,
                xliff: xliffFiles,
                configJson: serialized,
                srxFile: segmentation.mode === 'srx' ? srxFile : null,
                token: user?.token
            });
            const blob = decodeFilePayload(response);
            setMergedBlob(blob);
            setDownloadFileName(response.fileName || 'localized.xlsx');
            setConfigJson(serialized);
            if (userProfile) refreshProfile();

            // Auto-download
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = response.fileName || 'localized.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            // Update loading toast to success
            toast.success('Merge completed successfully.', { id: toastId });
        } catch (error) {
            // Update loading toast to error
            toast.error((error as Error).message || 'Merge failed.', { id: toastId });
        }
    };

    return (
        <div className="vertical-layout">
            <section className="card">
                <h2>Reference Data</h2>
                <div className="row-group">
                    <div className="field flex-grow">
                        <label htmlFor="merge-workbook">Original workbook (.xlsx)</label>
                        <input id="merge-workbook" type="file" accept=".xlsx" onChange={handleReferenceUpload} />
                        {referenceFile && (
                            <div className="message success">
                                Loaded <strong>{referenceFile.name}</strong> — {sheetSummaries.length} sheet
                                {sheetSummaries.length === 1 ? '' : 's'} detected.
                            </div>
                        )}
                    </div>
                </div>

                <div className="row-group">
                    <div className="field flex-grow">
                        <label htmlFor="merge-xliff">Translated XLIFF(s)</label>
                        <input
                            id="merge-xliff"
                            type="file"
                            multiple
                            accept=".xlf,.xliff,.xml"
                            onChange={(event) => {
                                const files = Array.from(event.target.files ?? []);
                                // Enforce free tier limit: max 2 XLIFF files
                                if (!user && files.length > 2) {
                                    toast.error('Free users are limited to 2 XLIFF files. Please sign in for unlimited files.'
                                    );
                                    setXliffFiles(files.slice(0, 2));
                                    // Reset file input to show correct count
                                    event.target.value = '';
                                } else {
                                    setXliffFiles(files);
                                }
                            }}
                        />
                        {xliffFiles.length > 0 && (
                            <div className="message success">
                                Selected {xliffFiles.length} file{xliffFiles.length === 1 ? '' : 's'}:
                                <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                                    {xliffFiles.map((f, i) => (
                                        <li key={i}>{f.name}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                <div className="divider" />

                <div className="row-group two-col">
                    <div className="field">
                        <label htmlFor="merge-config-upload">Import Config File (JSON/YAML)</label>
                        <input
                            id="merge-config-upload"
                            type="file"
                            accept=".json,.yaml,.yml"
                            onChange={handleConfigUpload}
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="merge-config-import">Or Paste Config Content</label>
                        <textarea
                            id="merge-config-import"
                            placeholder="Paste config JSON/YAML here..."
                            value={configImportText}
                            onChange={(event) => setConfigImportText(event.target.value)}
                            rows={1}
                            style={{ minHeight: '42px' }}
                        />
                    </div>
                </div>
                <button className="button secondary" type="button" onClick={() => applyConfigImport()}>
                    Apply Config
                </button>



            </section>

            <section className="card">
                <h2>Global Preferences</h2>
                <div className="input-stack">
                    <div className="field">
                        <label htmlFor="merge-src-lang">Source language</label>
                        <input
                            id="merge-src-lang"
                            type="text"
                            value={globalOptions.srcLang}
                            onChange={(event) => setGlobalOptions((prev) => ({ ...prev, srcLang: event.target.value }))}
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="merge-target-locale">Target locale preference (optional)</label>
                        <input
                            id="merge-target-locale"
                            type="text"
                            value={globalOptions.targetLocale ?? ''}
                            onChange={(event) =>
                                setGlobalOptions((prev) => ({
                                    ...prev,
                                    targetLocale: event.target.value.trim() ? event.target.value : undefined
                                }))
                            }
                        />
                    </div>
                    <div className="toggle-row">
                        <label htmlFor="merge-overwrite">Overwrite target cells</label>
                        <input
                            id="merge-overwrite"
                            type="checkbox"
                            checked={globalOptions.overwrite}
                            onChange={(event) =>
                                setGlobalOptions((prev) => ({ ...prev, overwrite: event.target.checked }))
                            }
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="merge-placement">Target column placement</label>
                        <select
                            id="merge-placement"
                            value={globalOptions.insertTargetPlacement}
                            onChange={(event) =>
                                setGlobalOptions((prev) => ({
                                    ...prev,
                                    insertTargetPlacement: event.target.value as GlobalOptions['insertTargetPlacement']
                                }))
                            }
                        >
                            <option value="insertAfterSource">Insert after source</option>
                            <option value="appendToSheetEnd">Append to sheet end</option>
                        </select>
                    </div>
                </div>
                <div className="divider" />
                <h3>Segmentation</h3>
                <div className="stepper">
                    <div className="toggle-row">
                        <label htmlFor="merge-seg-enabled">Enable segmentation</label>
                        <input
                            id="merge-seg-enabled"
                            type="checkbox"
                            checked={segmentation.enabled}
                            onChange={(event) => setSegmentation((prev) => ({ ...prev, enabled: event.target.checked }))}
                        />
                    </div>
                    {segmentation.enabled && (
                        <>
                            <div className="field">
                                <label htmlFor="merge-seg-mode">Segmentation rules</label>
                                <select
                                    id="merge-seg-mode"
                                    value={segmentation.mode}
                                    onChange={(event) =>
                                        setSegmentation((prev) => ({ ...prev, mode: event.target.value as 'builtin' | 'srx' }))
                                    }
                                >
                                    <option value="builtin">Built-in</option>
                                    <option value="srx" disabled={userProfile ? !userProfile.limits.customSrx : true}>
                                        Custom SRX {(!userProfile || !userProfile.limits.customSrx) && '(Upgrade required)'}
                                    </option>
                                </select>
                            </div>
                            {segmentation.mode === 'srx' && (
                                <div className="field">
                                    <label htmlFor="merge-srx">SRX rules file</label>
                                    <input id="merge-srx" type="file" accept=".srx,.xml" onChange={(event) => setSrxFile(event.target.files?.[0] ?? null)} />
                                    {srxFile && <small>Loaded: {srxFile.name}</small>}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

            {sheetSummaries.length > 0 && (
                <section className="card">
                    <h2>Sheet Mapping</h2>
                    <div className="stepper">
                        {sheetSummaries.map((summary, index) => {
                            const mapping = sheetMappings[index] ?? createDefaultMapping(summary);
                            return (
                                <div className="sheet-card" key={summary.name}>
                                    <div className="sheet-card-header">
                                        <strong>{summary.name}</strong>
                                        <label className="toggle-row" style={{ gap: '0.5rem' }}>
                                            <span>Include</span>
                                            <input
                                                type="checkbox"
                                                checked={mapping.enabled}
                                                onChange={(event) =>
                                                    setSheetMappings((prev) =>
                                                        updateSheetAtIndex(prev, index, { enabled: event.target.checked })
                                                    )
                                                }
                                            />
                                        </label>
                                    </div>
                                    <div className="sheet-card-body">
                                        <div className="field">
                                            <label>Source column</label>
                                            <select
                                                value={mapping.sourceColumn}
                                                onChange={(event) =>
                                                    setSheetMappings((prev) =>
                                                        updateSheetAtIndex(prev, index, { sourceColumn: event.target.value })
                                                    )
                                                }
                                            >
                                                {summary.columns.map((col) => (
                                                    <option key={col.letter} value={col.letter}>
                                                        {col.letter} {col.header ? `— ${col.header}` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="field">
                                            <label>Target locales</label>
                                            <div className="pill-group">
                                                {mapping.targetColumns.map((target, tIndex) => (
                                                    <div className="chip" key={tIndex}>
                                                        <input
                                                            type="text"
                                                            placeholder="locale"
                                                            value={target.locale}
                                                            onChange={(event) => {
                                                                const next = [...mapping.targetColumns];
                                                                next[tIndex] = { ...next[tIndex], locale: event.target.value };
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, { targetColumns: next })
                                                                );
                                                            }}
                                                        />
                                                        <select
                                                            value={target.column}
                                                            onChange={(event) => {
                                                                const next = [...mapping.targetColumns];
                                                                next[tIndex] = { ...next[tIndex], column: event.target.value };
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, { targetColumns: next })
                                                                );
                                                            }}
                                                        >
                                                            <option value="">Select column</option>
                                                            {summary.columns.map((col) => (
                                                                <option key={col.letter} value={col.letter}>
                                                                    {col.letter}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const next = mapping.targetColumns.filter((_, removeIndex) => removeIndex !== tIndex);
                                                                setSheetMappings((prev) =>
                                                                    updateSheetAtIndex(prev, index, { targetColumns: next })
                                                                );
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <button
                                                className="button secondary"
                                                type="button"
                                                onClick={() => {
                                                    const maxTargets = userProfile ? userProfile.limits.maxTargetLangs : ANONYMOUS_LIMITS.maxTargetLangs;
                                                    if (mapping.targetColumns.length >= maxTargets) {
                                                        toast.error(`Limit reached: Max ${maxTargets} target languages.`);
                                                        return;
                                                    }
                                                    setSheetMappings((prev) =>
                                                        updateSheetAtIndex(prev, index, {
                                                            targetColumns: [...mapping.targetColumns, { locale: '', column: '' }]
                                                        })
                                                    );
                                                }}
                                            >
                                                + Add locale
                                            </button>
                                        </div>
                                        <div className="toggle-row">
                                            <label>Preserve styles</label>
                                            <input
                                                type="checkbox"
                                                checked={mapping.preserveStyles}
                                                onChange={(event) =>
                                                    setSheetMappings((prev) =>
                                                        updateSheetAtIndex(prev, index, { preserveStyles: event.target.checked })
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            <section className="card">
                <h2>Finalize</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                    Ready to generate your localized Excel file? This will merge all translations back into the original workbook structure.
                </p>

                <div className="panel-actions">
                    <button
                        className="button large"
                        type="button"
                        onClick={runMerge}
                    >
                        Download localized workbook
                    </button>
                </div>
            </section>
        </div>
    );
};

export const ConvertPage: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<'extract' | 'merge'>('extract');
    const statusMessage = useMemo(() => 'Excel ↔ XLIFF Localization Studio', []);
    const [userProfile, setUserProfile] = useState<User | null>(null);

    const fetchProfile = async () => {
        try {
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/user`, {
                headers: { Authorization: `Bearer ${user?.token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUserProfile(data);
            }
        } catch (err) {
            console.error('Failed to fetch profile', err);
        }
    };

    useEffect(() => {
        if (user?.token) {
            fetchProfile();
        } else {
            setUserProfile(null);
        }
    }, [user]);

    return (
        <div className="page-container" style={{ maxWidth: '1000px' }}>
            <section className="hero">
                <h1 className="hero-title">{statusMessage}</h1>
                <p className="hero-subtitle">
                    Export segmented XLIFFs and merge translations with native multi-lingual support, rich Excel filtering, and simple JSON/YAML configuration.
                </p>
            </section>
            <div className="tab-bar">
                <button
                    className={`tab-button ${activeTab === 'extract' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActiveTab('extract')}
                >
                    XLSX → XLIFF
                </button>
                <button
                    className={`tab-button ${activeTab === 'merge' ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActiveTab('merge')}
                >
                    XLIFF → XLSX
                </button>
            </div>
            {activeTab === 'extract' ? (
                <ExcelToXliffPanel userProfile={userProfile} refreshProfile={fetchProfile} />
            ) : (
                <XliffToExcelPanel userProfile={userProfile} refreshProfile={fetchProfile} />
            )}
        </div>
    );
};
